import React, { useEffect, useId, useState } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { faBinoculars } from '@fortawesome/free-solid-svg-icons'
import { useLeadFunnelBlocksPermissions } from './lead_funnel_blocks/useLeadFunnelBlocksPermissions'
import { useBlockChangeRequestApi } from './lead_funnel_blocks/useBlockChangeRequestApi'
import { useAuthReady } from './lead_funnel_blocks/useAuthReady'
import {
  CHANGE_REQUEST_STATUSES,
  statusMeta,
} from './lead_funnel_blocks/blockChangeHelpers'
import {
  pageFromUrl,
  pagerMeta,
} from './lead_funnel_blocks/blocksExplorerHelpers'
import { FormatISODateTime } from '../../helpers/formatting'
import LayoutSidebar from '../../components/LayoutSidebar'
import Loading from '../../components/Loading'
import NoRecord from '../../components/elements/NoRecord'
import TableHeader from '../../components/TableHeader'
import { IconWithHelperText } from '../../components/TableRow'
import Pagination from '../../components/Pagination'

const COLUMNS = [
  { column_name: 'title', label_name: 'Request', list_size: 12 },
  { column_name: 'status', label_name: 'Status', list_size: 4 },
  { column_name: 'created_by_user_name', label_name: 'Author', list_size: 5 },
  { column_name: 'updated_at', label_name: 'Updated', list_size: 6 },
]

export default function LeadFunnelBlockChangeRequests() {
  const { permissions, currentUserId, isSpoofing } =
    useLeadFunnelBlocksPermissions()
  const navi = useNavigate()
  const location = useLocation()
  const startId = useId()
  const ready = useAuthReady()
  const api = useBlockChangeRequestApi()
  // The filters live in the URL so coming back from a request restores the same tab and page.
  const [searchParams, setSearchParams] = useSearchParams()
  const status = CHANGE_REQUEST_STATUSES.includes(searchParams.get('status'))
    ? searchParams.get('status')
    : null
  const mineOnly = searchParams.get('mine') === '1'
  const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1)
  const setQuery = (changes) => {
    const next = { status, mine: mineOnly, page, ...changes }
    const params = {}
    if (next.status) params.status = next.status
    if (next.mine) params.mine = '1'
    if (next.page > 1) params.page = String(next.page)
    setSearchParams(params, { replace: true })
  }
  const [list, setList] = useState(null)
  const [counts, setCounts] = useState({})
  const [listFailed, setListFailed] = useState(false)
  const [listAttempt, setListAttempt] = useState(0)
  const [title, setTitle] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  // Until the user is known "Only mine" cannot be applied, so it is neither sent nor shown as on.
  const authorId = mineOnly && currentUserId !== null ? currentUserId : null

  useEffect(() => {
    if (!ready) return
    let isStale = false
    setListFailed(false)
    // Rows of the previous query must not stay clickable under the new one.
    setList(null)
    api
      .list({ status, authorId, page: page > 1 ? page : null })
      .then((body) => {
        if (isStale) return
        if (body) {
          setList(body)
          setCounts(body.status_counts ?? {})
        } else setListFailed(true)
      })
    return () => {
      isStale = true
    }
    // useBlockChangeRequestApi() recreates its methods every render; they do not belong here.
  }, [ready, status, authorId, page, listAttempt])

  const create = () => {
    setIsCreating(true)
    api
      .create({ title: title.trim(), description: null })
      .then(
        (data) =>
          data && navi(`/lead_funnel_blocks?cr=${data.change_request.id}`)
      )
      .finally(() => setIsCreating(false))
  }

  // status_counts ignore the author filter, so they are hidden rather than misread as "mine".
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
  const tabs = [{ key: null, label: 'All', count: total }].concat(
    CHANGE_REQUEST_STATUSES.map((key) => ({
      key,
      label: statusMeta(key).label,
      count: counts[key] ?? 0,
    }))
  )
  const lastPage = list
    ? Math.max(1, Math.ceil(list.meta.total / list.meta.per_page))
    : 1

  const content = (
    <div className="lox-block-change">
      <div className="level mb-3">
        <div className="level-left">
          <Link className="is-size-7" to="/lead_funnel_blocks">
            ← Blocks Explorer
          </Link>
        </div>
        {isSpoofing && (
          <div className="level-right">
            <p className="is-size-7 has-text-grey" data-testid="cr-read-only">
              Read-only while viewing as another user
            </p>
          </div>
        )}
        {permissions.canCreate && !isSpoofing && (
          <div className="level-right">
            <div className="field has-addons">
              <div className="control">
                <input
                  className="input is-small"
                  placeholder="New change request title"
                  maxLength={200}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  data-testid="cr-new-title"
                />
              </div>
              <div className="control">
                <button
                  type="button"
                  className="button is-small is-primary"
                  disabled={isCreating || title.trim() === ''}
                  onClick={create}
                  data-testid="cr-new-create"
                >
                  Start draft
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="tabs is-small mb-2">
        <ul>
          {tabs.map((tab) => (
            <li
              key={tab.key ?? 'all'}
              className={status === tab.key ? 'is-active' : ''}
            >
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault()
                  setQuery({ status: tab.key, page: 1 })
                }}
                data-testid={`cr-list-tab-${tab.key ?? 'all'}`}
              >
                {tab.label}
                {authorId === null && (
                  <span className="tag is-light lox-no-border ml-1">
                    {tab.count}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <label className="checkbox is-size-7 mb-2 is-block">
        <input
          type="checkbox"
          className="mr-1"
          checked={authorId !== null}
          disabled={currentUserId === null}
          onChange={(event) => {
            setQuery({ mine: event.target.checked, page: 1 })
          }}
          data-testid="cr-list-mine"
        />
        Only mine
      </label>

      {listFailed && (
        <div className="has-text-centered p-5" data-testid="cr-list-load-error">
          <p className="has-text-danger mb-2">
            The change requests could not be loaded.
          </p>
          <button
            type="button"
            className="button is-small"
            onClick={() => setListAttempt((attempt) => attempt + 1)}
            data-testid="cr-list-load-retry"
          >
            Retry
          </button>
        </div>
      )}
      {!list && !listFailed && <Loading />}
      {list && !listFailed && list.data.length === 0 && <NoRecord />}
      {list && !listFailed && list.data.length > 0 && (
        <div className="lox-hooktable table-container mb-0 position-relative">
          <div className="list-table-container">
            <table
              className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button"
              data-testid="cr-list-table"
            >
              <TableHeader columns={COLUMNS} startId={startId} hasView />
              <tbody>
                {list.data.map((changeRequest) => {
                  const meta = statusMeta(changeRequest.status)
                  const path = `/lead_funnel_block_change_requests/${changeRequest.id}`
                  const linkState = { listSearch: location.search }
                  const open = () => navi(path, { state: linkState })
                  const openOnEnter = (event) => {
                    if (event.target !== event.currentTarget) return
                    if (event.key === 'Enter') open()
                  }
                  return (
                    <tr
                      key={changeRequest.id}
                      className="lox-blocks-explorer__row"
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={openOnEnter}
                      data-testid={`cr-list-row-${changeRequest.id}`}
                    >
                      <th className="fixed sticky">
                        <Link
                          to={path}
                          state={linkState}
                          className="button is-primary lox-view-button"
                          aria-label={`View CR-${changeRequest.id}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <IconWithHelperText
                            icon={faBinoculars}
                            helperText="View Record"
                          />
                        </Link>
                      </th>
                      <td>
                        CR-{changeRequest.id} {changeRequest.title}
                      </td>
                      <td>
                        <span className={`tag lox-no-border ${meta.tagClass}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td>{changeRequest.created_by_user_name ?? '—'}</td>
                      <td>{FormatISODateTime(changeRequest.updated_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {lastPage > 1 && (
        <div data-testid="cr-list-pager">
          <Pagination
            meta={pagerMeta({ page, totalPages: lastPage })}
            setPartialLoad={() => {}}
            paginateCallback={(url) => setQuery({ page: pageFromUrl(url) })}
          />
        </div>
      )}
    </div>
  )

  return (
    <LayoutSidebar contentTitle="Block change requests" content={content} />
  )
}
